import type { StudentProps } from "../types/common";
import type { classDataProps } from "../types/common";
import { Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";

export const StudentSettingModal = ({
  open,
  onClose,
  studentsData,
  selectClass,
  handleUpdateStudent,
}: {
  open: boolean;
  onClose: () => void;
  studentsData: StudentProps[];
  selectClass: classDataProps | null;
  handleUpdateStudent: (
    student: StudentProps,
    updateData: {
      name: string;
      seatNumber: string;
    }
  ) => void;
}) => {
  const [isEdit, setIsEdit] = useState<null | number>(null);
  const [updateData, setUpdateData] = useState<{
    name: string;
    seatNumber: string;
  }>({
    name: "",
    seatNumber: "",
  });
  const handleEditStudent = (index: number) => {
    setIsEdit(index);
    setUpdateData({
      name: studentsData[index].studentName,
      seatNumber: studentsData[index].studentSeatNo,
    });
  };
  return (
    <div
      onClick={onClose}
      className={`fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-20 p-4 ${
        open ? "block" : "hidden"
      }`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white md:p-10 p-4 rounded-lg flex flex-col gap-4 max-w-[800px] w-full max-h-[90vh] overflow-hidden"
      >
        <p className="text-lg font-medium text-primary-light">
          班級: {selectClass?.className}
        </p>
        <div className="flex-1 overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full bg-gray-100 rounded-lg shadow-md">
              <thead className="bg-primary-light lg:text-lg md:text-base text-sm text-nowrap sticky top-0 z-10">
                <tr>
                  <th className="lg:px-4 md:px-2 px-1 py-2 text-white text-center">姓名</th>
                  <th className="lg:px-4 md:px-2 px-1 py-2 text-white text-center">座號</th>
                  <th className="lg:px-4 md:px-2 px-1 py-2 text-white text-center">
                    電子郵件信箱
                  </th>
                  <th className="lg:px-4 md:px-2 px-1 py-2 text-white text-center">功能</th>
                </tr>
              </thead>
              <tbody className="md:text-base text-sm">
            {studentsData &&
              studentsData.length > 0 &&
              studentsData.map((student: StudentProps, index: number) => {
                return (
                  <tr key={student._id} className="text-center text-nowrap">
                    <td>
                      <div className="flex justify-center items-center md:p-2 p-1">
                        <input
                          value={isEdit === index ? updateData.name : student.studentName}
                          disabled={isEdit !== index}
                          className={`rounded-sm p-1 w-[5em] text-center ${
                            isEdit === index ? "border border-black/70" : ""
                          }`}
                          onChange={(e) => {
                            setUpdateData({
                              ...updateData,
                              name: e.target.value,
                            });
                          }}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="flex justify-center items-center md:p-2 p-1">
                        <input
                          value={isEdit === index ? updateData.seatNumber : student.studentSeatNo}
                          disabled={isEdit !== index}
                          className={`rounded-sm p-1 w-[3em] text-center ${
                            isEdit === index ? "border border-black/70" : ""
                          }`}
                          onChange={(e) => {
                            const value = e.target.value;
                            // 只允許數字，移除非數字字符
                            let numericValue = value.replace(/\D/g, '');
                            
                            // 限制最大值為999
                            if (numericValue && parseInt(numericValue) > 999) {
                              numericValue = '999';
                            }
                            
                            setUpdateData({
                              ...updateData,
                              seatNumber: numericValue,
                            });
                          }}
                          onKeyDown={(e) => {
                            // 阻止非數字字符的輸入
                            if (!/[0-9]/.test(e.key) && 
                                !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          inputMode="numeric"
                        />
                      </div>
                    </td>
                    <td>
                      <div
                        title={student.studentEmail}
                        className="flex justify-center items-center md:p-2 p-1 group relative"
                      >
                        <input
                          defaultValue={student.studentEmail}
                          disabled={true}
                          className={`rounded-sm p-1 md:w-[18em] sm:w-[10em] w-[6em] text-ellipsis text-center`}
                        />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                          {student.studentEmail}
                        </div>
                      </div>
                    </td>
                    <td>
                      {isEdit === index ? (
                        <div className="flex justify-center items-center sm:flex-row flex-col gap-1 p-1">
                          <Button
                            sx={{
                              fontSize: "14px",
                              padding: "2px 8px",
                              border: "none",
                            }}
                            variant="contained"
                            color="success"
                            onClick={() => {
                              handleUpdateStudent(student, updateData);
                              // 更新成功後重置編輯狀態
                              setIsEdit(null);
                              setUpdateData({ name: "", seatNumber: "" });
                            }}
                            disabled={
                              updateData.name.trim() === "" ||
                              updateData.seatNumber.trim() === "" ||
                              (updateData.name === student.studentName &&
                              updateData.seatNumber === student.studentSeatNo)
                            }
                          >
                            更新
                          </Button>
                          <Button
                            sx={{
                              fontSize: "14px",
                              padding: "2px 8px",
                              border: "none",
                            }}
                            variant="contained"
                            color="error"
                            onClick={() => {
                              setIsEdit(null);
                            }}
                          >
                            取消
                          </Button>
                        </div>
                      ) : (
                        <Button
                          sx={{
                            fontSize: "16px",
                            padding: "2px 8px",
                            border: "none",
                          }}
                          variant="outlined"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditStudent(index)}
                        >
                          <span className="lg:inline hidden">編輯</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};